import { Map as ImmutableMap } from 'immutable';
import emptyFunction from 'react-utils/emptyFunction';
import { AsyncQueryStatus, POLL_RATE, REPORT_SOURCE } from '../../../constants/snowflakeQuery';
import { fetchWithQueryId, fetchWithReportDefinition, fetchWithReportId } from './API';
import { AsyncQueryCancelled, AsyncQueryError, AsyncQueryFailed, AsyncQueryTimeout } from './records';
export const formatDataSet = res => {
  const dataSet = res.get('dataSet');
  return dataSet.merge({
    dataAge: res.getIn(['asyncQueryResponseMeta', 'dataAge']),
    queryId: res.getIn(['asyncQueryResponseMeta', 'queryId'])
  });
};
/**
 * Start an async query, then recursively poll async query results until complete.
 * @param {reportWithOptions} reportWithOptions
 * @param {{
 *     onTick: function,
 *     reportId: string,
 *     lastQuery: ImmutableMap
 * }} params an object containing an onTick callback to be called on every tick,
 * the reportId to query for (if applicable), and the previous query, which
 * can be used to start the query outside this function (also used in recursion)
 * @returns {Promise<ImmutableMap>}
 */
export const pollRaaSAPI = (reportWithOptions, params = {}) => new Promise((resolvePromise, rejectPromise) => {
  const {
    onTick = emptyFunction,
    // Optional queryId to reuse existing cached snowflake query
    queryId: initialQueryId,
    reportId: maybeReportId,
    lastReportingApiResponse = ImmutableMap()
  } = params;
  if (lastReportingApiResponse.get('reportSource') === REPORT_SOURCE.CRM_SEARCH) {
    resolvePromise(lastReportingApiResponse.get('dataSet'));
    return;
  }
  const status = lastReportingApiResponse.getIn(['asyncQueryResponseMeta', 'queryStatus']);
  const queryId = lastReportingApiResponse.getIn(['asyncQueryResponseMeta', 'queryId']);
  const tick = nextReportingApiResponse => {
    const cancelled = onTick(nextReportingApiResponse, reportWithOptions);
    if (!cancelled) {
      pollRaaSAPI(reportWithOptions, Object.assign({}, params, {
        lastReportingApiResponse: nextReportingApiResponse
      })).then(resolvePromise, rejectPromise).catch(rejectPromise);
    } else {
      rejectPromise(new AsyncQueryCancelled(`Polling for Async query ${queryId} was cancelled `));
    }
  };
  switch (status) {
    // no status means we need to request a new query
    case undefined:
      {
        const fetchFromCache = reportWithOptions.getIn(['reportOptions', 'fetchFromCache']) !== undefined ? reportWithOptions.getIn(['reportOptions', 'fetchFromCache']) : true;
        const requester = maybeReportId && fetchFromCache ? () => fetchWithReportId(reportWithOptions, maybeReportId) : () => fetchWithReportDefinition(reportWithOptions, maybeReportId, initialQueryId);
        requester().then(tick, rejectPromise).catch(rejectPromise);
        break;
      }
    case AsyncQueryStatus.RUNNING:
      if (queryId) setTimeout(() => {
        fetchWithQueryId(reportWithOptions, queryId).then(tick, rejectPromise).catch(rejectPromise);
      }, POLL_RATE);
      break;
    case AsyncQueryStatus.SUCCESS:
      resolvePromise(formatDataSet(lastReportingApiResponse));
      break;
    case AsyncQueryStatus.FAILED:
      rejectPromise(new AsyncQueryFailed(`Async query ${queryId} failed`));
      break;
    case AsyncQueryStatus.CANCELLED:
      rejectPromise(new AsyncQueryCancelled(`Async query ${queryId} was cancelled`));
      break;
    case AsyncQueryStatus.TIMEOUT:
      rejectPromise(new AsyncQueryTimeout(`Async query ${queryId} timed out`));
      break;
    case AsyncQueryStatus.UNKNOWN:
    default:
      rejectPromise(new AsyncQueryError(`Async query ${queryId} errored with status ${status}`));
  }
});